// Social-platform registry (UI-facing). Cross-cutting + dependency-free so both
// the web and the API can describe the per-brand social connections consistently.
// The technical OAuth endpoint URLs live server-side (apps/api); only the fields
// the UI needs are here.

/**
 * How a brand authorizes a platform:
 *  - "oauth2"      — redirect the brand through the platform's OAuth consent
 *                    (requires the operator to register a developer app + set its
 *                    client id/secret in the API env).
 *  - "credentials" — the brand pastes credentials directly (e.g. a Bluesky
 *                    app-password); no developer app needed.
 */
export type SocialAuthType = "oauth2" | "credentials";

/** A single credential input shown for a "credentials" platform. */
export interface SocialCredentialField {
  key: string;
  label: string;
  type: "text" | "password";
  placeholder?: string;
}

/** A supported social platform, as described to the UI. */
export interface SocialPlatformDef {
  /** Stable platform key (matches SocialConnection.platform + the API routes). */
  key: string;
  label: string;
  authType: SocialAuthType;
  /** Brand color (hex) for the platform chip. */
  color: string;
  /** Developer-docs URL (shown as a help link). */
  docsUrl: string;
  /** OAuth scopes requested (informational; shown as "akan meminta izin: …"). */
  scopes?: string[];
  /** Inputs for a "credentials" platform (e.g. Bluesky identifier + app-password). */
  credentialFields?: SocialCredentialField[];
  /** Short operator-facing caveat (audit needed, paid API, etc.). */
  note?: string;
  /**
   * Whether a draft can be auto-published to this platform today. Platforms that
   * still need page/board/location selection or a video pipeline are false until
   * their publisher lands (see @app/core publishToSocial).
   */
  supportsDraftPublish?: boolean;
}

/**
 * The platforms a brand can connect, besides the built-in Instagram + WordPress.
 * Order is the display order. Keep `key` in sync with the API's server-side
 * OAuth config map and SocialConnection.platform.
 */
export const SOCIAL_PLATFORMS: SocialPlatformDef[] = [
  {
    key: "threads",
    label: "Threads",
    authType: "oauth2",
    color: "#000000",
    docsUrl: "https://developers.facebook.com/docs/threads",
    scopes: ["threads_basic", "threads_content_publish"],
  },
  {
    key: "facebook",
    label: "Facebook Page",
    authType: "oauth2",
    color: "#1877F2",
    docsUrl: "https://developers.facebook.com/docs/pages-api/",
    scopes: ["pages_show_list", "pages_read_engagement", "pages_manage_posts"],
    note: "Butuh Facebook Page; perlu App Review untuk publikasi.",
  },
  {
    key: "bluesky",
    label: "Bluesky",
    authType: "credentials",
    color: "#0085FF",
    docsUrl: "https://docs.bsky.app/",
    credentialFields: [
      {
        key: "identifier",
        label: "Handle atau email",
        type: "text",
        placeholder: "brand.bsky.social",
      },
      {
        key: "appPassword",
        label: "App password",
        type: "password",
        placeholder: "xxxx-xxxx-xxxx-xxxx",
      },
    ],
    note: "Pakai App Password (Settings → App Passwords di Bluesky), bukan password utama.",
    supportsDraftPublish: true,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    authType: "oauth2",
    color: "#0A66C2",
    docsUrl:
      "https://learn.microsoft.com/en-us/linkedin/marketing/community-management/community-management-overview",
    scopes: ["r_organization_social", "w_organization_social"],
    note: "Community Management API: perlu entitas badan hukum + persetujuan LinkedIn.",
  },
  {
    key: "pinterest",
    label: "Pinterest",
    authType: "oauth2",
    color: "#E60023",
    docsUrl: "https://developers.pinterest.com/docs/api/v5/introduction/",
    scopes: ["boards:read", "pins:read", "pins:write"],
    note: "Butuh akun bisnis + review aplikasi.",
  },
  {
    key: "gbp",
    label: "Google Business Profile",
    authType: "oauth2",
    color: "#4285F4",
    docsUrl: "https://developers.google.com/my-business",
    scopes: ["https://www.googleapis.com/auth/business.manage"],
    note: "Untuk bisnis lokal (muncul di Google Maps/Search).",
  },
  {
    key: "tiktok",
    label: "TikTok",
    authType: "oauth2",
    color: "#010101",
    docsUrl: "https://developers.tiktok.com/doc/content-posting-api-get-started",
    scopes: ["user.info.basic", "video.publish"],
    note: "Perlu audit TikTok; sebelum lolos hanya bisa posting privat (SELF_ONLY).",
  },
  {
    key: "x",
    label: "X (Twitter)",
    authType: "oauth2",
    color: "#000000",
    docsUrl: "https://docs.x.com/x-api",
    scopes: ["tweet.read", "tweet.write", "users.read", "offline.access"],
    note: "API X berbayar (model pay-per-use). Publikasi gambar menyusul (teks dulu).",
    supportsDraftPublish: true,
  },
];

/** Set of valid platform keys (for validation). */
export const SOCIAL_PLATFORM_KEYS: string[] = SOCIAL_PLATFORMS.map((p) => p.key);

/** Look up a platform definition by key. */
export function socialPlatform(key: string): SocialPlatformDef | undefined {
  return SOCIAL_PLATFORMS.find((p) => p.key === key);
}

/**
 * Per-brand connection status for one platform, as returned by the API. Never
 * includes credentials.
 */
export interface SocialConnectionDTO {
  platform: string;
  /** True when this brand has stored credentials for the platform. */
  connected: boolean;
  /**
   * True when the platform is usable on this server: for "oauth2" the operator
   * has configured the developer-app client id/secret; for "credentials" always
   * true. A disabled "Hubungkan" button + hint is shown when false.
   */
  configured: boolean;
  /** Platform handle/name to display when connected. */
  externalName: string | null;
  /** ISO access-token expiry, when known. */
  expiresAt: string | null;
  /** Granted scopes (informational). */
  scopes: string[];
}

/** Lifecycle of a single draft→platform publication. */
export type PublicationStatus = "pending" | "published" | "failed";

/** One platform publication of a draft, as returned by the API. */
export interface DraftPublicationDTO {
  platform: string;
  status: PublicationStatus;
  /** Platform-side post id/uri once published. */
  externalPostId: string | null;
  /** Public permalink to the post, when available. */
  externalUrl: string | null;
  /** Failure reason when status is "failed". */
  errorMsg: string | null;
  updatedAt: string;
}
